"use client"

import { CollectionScopeProvider } from "@/app/collections/store/scope-provider"
import { useEvent } from "@/app/hooks/use-event"
import { ArrowDownIcon } from "@/app/icons/arrow-down"
import { useUserOwnedNft } from "@/app/nft/hooks/use-user-owned-nft"
import { NftsToSellScopeReatom } from "@/app/nft/sell/selection/scope.reatom"
import { useSellState } from "@/app/nft/sell/use-sell-state"
import { PriceWrapper } from "@/app/price"

import type { Nft } from "@anaconda/core/nft/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { ScopeProvider } from "bunshi/react"
import { useMemo, useState } from "react"
import { NftSellDialog } from "./nft-sell-dialog"

interface Props {
  nft: Nft
  variant?: "default" | "secondary"
  className?: string
}

export const NftSellButton = reatomComponent<Props>(
  ({ nft, variant = "secondary", className }) => {
    const [openDialog, setOpenDialog] = useState(false)
    const { userOwnedNft } = useUserOwnedNft(nft)

    // Convert NFT to NftMiniature for the scope
    const nftMiniature = useMemo(
      () => ({
        id: nft.id,
        collection: nft.collection,
        image: nft.image,
        name: nft.name,
        lastSellPrice: nft.lastSellPrice,
      }),
      [nft],
    )

    // Only show sell button if user owns the NFT
    if (!userOwnedNft) {
      return null
    }

    return (
      <CollectionScopeProvider state={{ collectionId: nft.collection.id }}>
        <ScopeProvider scope={NftsToSellScopeReatom} value={[nftMiniature]}>
          <NftSellButtonContent
            nft={nft}
            openDialog={openDialog}
            setOpenDialog={setOpenDialog}
            variant={variant}
            className={className}
          />
        </ScopeProvider>
      </CollectionScopeProvider>
    )
  },
  "NftSellButton",
)

const NftSellButtonContent = reatomComponent<
  Props & {
    openDialog: boolean
    setOpenDialog: (open: boolean) => void
  }
>(({ nft, openDialog, setOpenDialog, variant, className }) => {
  // Convert NFT to NftMiniature array for sell state
  const nftsToSell = useMemo(
    () => [
      {
        id: nft.id,
        collection: nft.collection,
        image: nft.image,
        name: nft.name,
        lastSellPrice: nft.lastSellPrice,
      },
    ],
    [nft],
  )

  const { bestSellOffer, readyToSell } = useSellState(nftsToSell)

  const onClick = useEvent(() => {
    setOpenDialog(true)
  })

  // Hide button if there are no bids
  if (!readyToSell) {
    return null
  }

  return (
    <>
      <NftSellDialog nft={nft} onOpenChange={setOpenDialog} open={openDialog} />

      <SellButtonContent
        onClick={onClick}
        variant={variant}
        className={className}
        offer={bestSellOffer}
      />
    </>
  )
}, "NftSellButtonContent")

function SellButtonContent({
  variant = "secondary",
  className,
  offer,
  ...props
}: React.ComponentProps<typeof Button> & {
  variant?: "default" | "secondary"
  offer?: { price: PriceValue }
}) {
  const buttonVariant = variant === "secondary" ? "secondary" : "default"

  return (
    <Button variant={buttonVariant} className={className} {...props}>
      <div className="flex items-center gap-2">
        {variant === "default" && (
          <div className="rounded-full bg-destructive p-1 text-black">
            <ArrowDownIcon className="rotate-180 [&&]:h-[10px] [&&]:w-[10px]" />
          </div>
        )}
        <span>
          <Trans>Accept bid</Trans>
        </span>
        {offer && variant === "default" && (
          <span className="ml-2">
            <PriceWrapper currencyClassName="text-white" {...offer.price} />
          </span>
        )}
      </div>
    </Button>
  )
}
